/**
 * Labstep
 *
 * @module components/Metadata/Action/Edit
 * @desc Action to edit metadata
 */

import { EntityActionEdit } from 'labstep-web/components/Entity/Action/Edit';
import { EntityActionEditModal } from 'labstep-web/components/Entity/Action/Edit/Modal';
import { MetadataFormEdit } from 'labstep-web/components/Metadata/Form/Edit';
import ProtocolChildRouter from 'labstep-web/containers/Router/ProtocolChild';
import { ActionComponentModalContainer as ActionComponent } from 'labstep-web/core/Action/Component';
import { Experiment } from 'labstep-web/models/experiment.model';
import { Protocol } from 'labstep-web/models/protocol.model';
import { getHumanReadableEntityName } from 'labstep-web/services/i18n.service';
import type React from 'react';
import { Message } from 'semantic-ui-react';
import type { MetadataActionEditProps } from './types';

export const MetadataActionEdit: React.FC<
  MetadataActionEditProps
> = ({
  entity,
  metadata,
  modalProps,
  actionComponentProps = {
    type: 'option',
    icon: 'cog',
    text: 'Configure Field',
  },
}) => {
  if (entity instanceof Protocol || entity instanceof Experiment) {
    return (
      <ProtocolChildRouter route="metadata_show" id={metadata.id}>
        {({ navigate }) => (
          <EntityActionEdit
            dataCy="components/Metadata/Action/Edit"
            entity={metadata}
            renderIfNoEditPermissions
            actionComponentProps={{
              onClick: navigate,
              ...actionComponentProps,
            }}
          />
        )}
      </ProtocolChildRouter>
    );
  }

  return (
    <EntityActionEditModal
      entity={metadata}
      header={`Edit ${metadata.label}`}
      viewComponent={({ toggleModal }) => (
        <ActionComponent
          dataCy="components/Metadata/Action/Edit"
          onClick={toggleModal}
          {...actionComponentProps}
        />
      )}
      content={({ toggleModal }) => (
        <div>
          {metadata.is_template && (
            <Message color="yellow">
              {`You are editing a template field. Any changes you make will apply to all ${getHumanReadableEntityName(
                entity.entityName,
                true,
              )} using this template.`}
            </Message>
          )}
          <MetadataFormEdit
            metadata={metadata}
            options={{ toast: true, onSuccess: toggleModal }}
          />
        </div>
      )}
      {...modalProps}
    />
  );
};
